import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  CardMedia,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TableSkeleton } from "../../components/Loading/Table/Table";
import { EGPaginator } from "../../components/Paginator/Paginator";
import EGLogo from "../../assets/MainLogo/Imagotipo_EG.svg";
import Etiquin from "../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { SearchField } from "./SearchField";
import {
  CATALOGUE_PREFIX,
  useCatalogueFilters,
} from "./useCatalogueFilters";
import {
  buildRequestPayload,
  buildSearchParams,
  hasActiveSearch,
  parseSearchCriteria,
  switchDomain,
  toggleFilterValue,
} from "./searchCriteria";

export const SearchScreen = () => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();

  // Panel de opciones: vive aquí porque abrirlo es lo que dispara la carga de
  // los catálogos del formulario
  const [openOptions, setOpenOptions] = useState(false);

  // Los catálogos no se piden al entrar a /search, solo cuando hacen falta:
  // al abrir el panel, o de inmediato si la URL ya trae filtros de catálogo
  // (sin ellos no se pueden pintar sus chips).
  const needsCatalogues =
    openOptions ||
    Array.from(searchParams.keys()).some((key) =>
      key.startsWith(CATALOGUE_PREFIX)
    );

  const { filters: catalogueFilters, isLoading: isLoadingCatalogues } =
    useCatalogueFilters({ enabled: needsCatalogues });

  // La URL manda: el criterio y el dominio siempre se derivan de ella
  const criteria = useMemo(
    () => parseSearchCriteria(searchParams, catalogueFilters),
    [searchParams, catalogueFilters]
  );
  const { domain } = criteria;
  const isSearching = hasActiveSearch(criteria);

  // Texto que el usuario está escribiendo (aún no ejecutado)
  const [draft, setDraft] = useState(criteria.q);

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Navegar con "atrás" o abrir un link compartido debe reflejarse en la UI.
  // criteria se re-memoiza justo cuando cambia la URL, así que sirve de disparador.
  useEffect(() => {
    setDraft(criteria.q);
  }, [criteria]);

  // Una sola petición por cambio de URL; el flag descarta respuestas viejas
  // para que una consulta lenta no pise el resultado de la siguiente.
  useEffect(() => {
    if (!isSearching) {
      setResults([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    criteria.domain
      .search(buildRequestPayload(criteria))
      .then((resp) => {
        if (cancelled) return;
        setResults(criteria.domain.extractResults(resp));
        setTotal(resp?.count || 0);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setResults([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [criteria, isSearching]);

  /** Escribe el criterio en la URL; cualquier cambio vuelve a la página 1 */
  const commit = useCallback(
    (changes = {}) => {
      setSearchParams(
        buildSearchParams({ ...criteria, q: draft, page: 1, ...changes })
      );
    },
    [criteria, draft, setSearchParams]
  );

  const handleChangeDomain = (type) => {
    if (type === criteria.type) return;
    // Campos y filtros no cruzan de dominio: pertenecen a otro endpoint
    setSearchParams(buildSearchParams(switchDomain({ ...criteria, q: draft }, type)));
  };

  const handleToggleField = (field) => {
    const isActive = criteria.fields.includes(field);

    // Campo único: elegir otro reemplaza; volver a tocar el activo no hace nada
    if (domain.singleField) {
      if (isActive) return;
      commit({ fields: [field] });
      return;
    }

    // Siempre debe quedar al menos un campo seleccionado
    if (isActive && criteria.fields.length === 1) return;

    commit({
      fields: isActive
        ? criteria.fields.filter((item) => item !== field)
        : [...criteria.fields, field],
    });
  };

  const handleToggleFilterValue = (key, value) => {
    commit({ advanced: toggleFilterValue(criteria.advanced, key, value) });
  };

  const handleSetFilterValue = (key, value) => {
    const advanced = { ...criteria.advanced };
    if (value) advanced[key] = value;
    else delete advanced[key];
    commit({ advanced });
  };

  const handleClear = () => {
    setDraft("");
    commit({ q: "" });
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams(buildSearchParams({ ...criteria, page: newPage }));
  };

  const handleLimitChange = (event) => {
    setSearchParams(
      buildSearchParams({
        ...criteria,
        limit: Number(event.target.value),
        page: 1,
      })
    );
  };

  const pageCount = Math.ceil(total / criteria.limit) || 0;
  const firstResult = (criteria.page - 1) * criteria.limit + 1;
  const DomainResults = domain.Results;

  const searchField = (variant) => (
    <SearchField
      variant={variant}
      domain={domain}
      onChangeDomain={handleChangeDomain}
      value={draft}
      onChange={setDraft}
      onSubmit={() => commit()}
      onClear={handleClear}
      fields={criteria.fields}
      onToggleField={handleToggleField}
      advanced={criteria.advanced}
      onToggleFilterValue={handleToggleFilterValue}
      onSetFilterValue={handleSetFilterValue}
      onClearAll={() => commit({ advanced: {} })}
      openOptions={openOptions}
      onToggleOptions={() => setOpenOptions((prev) => !prev)}
      isLoadingCatalogues={isLoadingCatalogues}
      autoFocus
    />
  );

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      {/* El buscador se monta una sola vez y solo cambia de variante: si se
          renderizara en ramas distintas, pasar del estado inicial a los
          resultados lo desmontaría y reiniciaría el panel de opciones. */}
      <Stack
        sx={
          isSearching
            ? { width: "100%", px: { xs: 0, md: 2 } }
            : {
                minHeight: "70vh",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }
        }
      >
        {!isSearching && (
          <CardMedia
            component="img"
            image={EGLogo}
            alt="Ethics Global"
            sx={{ width: { xs: 220, md: 300 }, mb: 4, objectFit: "contain" }}
          />
        )}

        <Box sx={{ width: "100%", maxWidth: isSearching ? "none" : 680 }}>
          {searchField(isSearching ? "compact" : "hero")}
        </Box>

        {!isSearching && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 3, textAlign: "center", maxWidth: 520 }}
          >
            {t("searchBar.search_page_hint")}
          </Typography>
        )}
      </Stack>

      {isSearching && (
        <Stack sx={{ width: "100%", px: { xs: 0, md: 2 } }}>
          <Box sx={{ mt: 3 }}>
            {isLoading ? (
              <TableSkeleton cols={isMobile ? 2 : 4} rows={6} />
            ) : results.length > 0 ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={12}
                  sx={{ px: { xs: 1, md: 2 }, mb: 0.5 }}
                >
                  {t("searchBar.results_count_in", {
                    count: total,
                    domain: t(domain.labelKey).toLowerCase(),
                  })}
                </Typography>

                <DomainResults results={results} query={criteria.q} />

                <Stack
                  sx={{
                    mt: 3,
                    flexFlow: "row wrap",
                    gap: 1,
                    alignItems: "center",
                    justifyContent: { xs: "center", md: "space-between" },
                  }}
                >
                  <Stack direction="row" alignItems="center">
                    <Typography variant="body2" fontSize={11} mr={0.5}>
                      {t("ReportsList.per_page")}:
                    </Typography>
                    <select
                      style={{ border: "none", fontSize: "11px" }}
                      name="limit"
                      value={criteria.limit}
                      onChange={handleLimitChange}
                    >
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                    </select>
                  </Stack>
                  <EGPaginator
                    onChange={handlePageChange}
                    count={pageCount}
                    page={criteria.page}
                    showFirstButton
                    showLastButton
                  />
                  <Typography variant="body2" fontSize={11}>
                    {`${t("ReportsList.results")}: ${firstResult} ${t(
                      "ReportsList.to"
                    )} ${firstResult + results.length - 1} ${t(
                      "ReportsList.of"
                    )} ${total}`}
                  </Typography>
                </Stack>
              </>
            ) : (
              <Stack sx={{ alignItems: "center", justifyContent: "center", py: 6 }}>
                <CardMedia
                  component="img"
                  image={Etiquin}
                  alt="not_found"
                  sx={{ height: 180, width: 180, objectFit: "contain" }}
                />
                <Typography variant="h6" color="secondary" mt={2}>
                  {t("ReportsList.no_results_found")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: "center", maxWidth: 420 }}
                >
                  {t("searchBar.no_results_hint")}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
